import { z } from 'zod';

/**
 * Ping Pong, timed-hit variant (spec §6.14.6, Phase 5a): two players at the table. The ball flies
 * on a schedule the server sets; the receiver presses "hit" when it reaches their paddle. The
 * server judges every press against the schedule and decides who wins each point.
 */

export const PONG_POINTS_TO_WIN = 5;
/** Ball waiting at the server's paddle before a serve. */
export const PONG_SERVE_DELAY_MS = 1_500;
/** Pause after a point before the next serve starts its delay. */
export const PONG_POINT_PAUSE_MS = 1_200;
/** First flight across the table; later shots get quicker. */
export const PONG_BASE_FLIGHT_MS = 1_300;
export const PONG_MIN_FLIGHT_MS = 550;
export const PONG_SPEEDUP_PER_SHOT_MS = 45;
/** Random spread of a flight, ± this much. */
export const PONG_FLIGHT_JITTER_MS = 120;
/** A press this close to the ball's arrival returns it. */
export const PONG_HIT_WINDOW_MS = 200;
/** A press this close is a smash: the return flies faster. */
export const PONG_SWEET_SPOT_MS = 60;
export const PONG_SMASH_BONUS_MS = 150;
/** Extra time the server waits for a late press to arrive before calling a miss. */
export const PONG_LATENCY_ALLOWANCE_MS = 250;
/** Accepted lead of a press over the server clock (client clock error). */
export const PONG_HIT_MAX_LEAD_MS = 250;
/** Accepted age of a press when it arrives. */
export const PONG_HIT_MAX_AGE_MS = 600;
/** How long the winner is shown before the table opens for the next match. */
export const PONG_GAME_OVER_MS = 6_000;

export type PongSide = 0 | 1;

export interface PongPlayer {
  /** Session id. */
  readonly id: string;
  readonly name: string;
  readonly score: number;
}

/** The ball leaves `from`'s paddle at `hitAt` and reaches the other paddle at `arriveAt`. */
export interface PongBall {
  readonly from: PongSide;
  readonly hitAt: number;
  readonly arriveAt: number;
  /** Shots in this rally so far (the serve is 0). */
  readonly shot: number;
  readonly smash: boolean;
}

export type PongPointReason = 'miss' | 'early' | 'forfeit';

/** The table's shared state (synced object data). */
export interface PingPongData {
  readonly game: 'pong';
  readonly phase: 'lobby' | 'playing' | 'over';
  /** Index 0 plays on the left, 1 on the right. */
  readonly players: readonly PongPlayer[];
  readonly ball: PongBall | null;
  /** Who serves next or served this rally. */
  readonly server: PongSide;
  readonly lastPoint: {
    readonly winner: PongSide;
    readonly reason: PongPointReason;
    readonly at: number;
  } | null;
  readonly winner: PongSide | null;
  /** Server time the result screen closes (`over`). */
  readonly overUntil: number;
}

export const pongHitSchema = z.object({ t: z.number().finite() }).strict();
export type PongHitInput = z.infer<typeof pongHitSchema>;

/** Flight time of shot number `shot`; `jitter` is uniform in [0, 1). */
export function pongFlightMs(shot: number, smash: boolean, jitter: number): number {
  const base = Math.max(PONG_MIN_FLIGHT_MS, PONG_BASE_FLIGHT_MS - shot * PONG_SPEEDUP_PER_SHOT_MS);
  const spread = Math.round((jitter * 2 - 1) * PONG_FLIGHT_JITTER_MS);
  const flight = base + spread - (smash ? PONG_SMASH_BONUS_MS : 0);
  return Math.max(PONG_MIN_FLIGHT_MS - PONG_SMASH_BONUS_MS, flight);
}

export type PongSwing = 'early' | 'return' | 'smash' | 'late';

/** Judges a press at `t` (server time) against an incoming ball. */
export function judgePongSwing(ball: PongBall, t: number): PongSwing {
  const offset = t - ball.arriveAt;
  if (offset < -PONG_HIT_WINDOW_MS) return 'early';
  if (offset > PONG_HIT_WINDOW_MS) return 'late';
  return Math.abs(offset) <= PONG_SWEET_SPOT_MS ? 'smash' : 'return';
}

/** The side that must hit the ball next. */
export function pongReceiver(ball: PongBall): PongSide {
  return ball.from === 0 ? 1 : 0;
}

/**
 * Where the ball is at `now`: `x` from 0 (left paddle) to 1 (right paddle) and a bounce height
 * `y` from 0 (table) to 1. Before the hit it rests at the hitter's paddle; after the arrival it
 * keeps flying past the paddle (a miss).
 */
export function pongBallPosition(ball: PongBall, now: number): { x: number; y: number } {
  const flight = Math.max(1, ball.arriveAt - ball.hitAt);
  const progress = Math.min(1.25, Math.max(0, (now - ball.hitAt) / flight));
  const x = ball.from === 0 ? progress : 1 - progress;
  // One bounce on the far half: an arc up to the bounce, then a smaller one to the paddle.
  const y =
    progress < 0.6
      ? Math.sin((progress / 0.6) * Math.PI) * 0.9
      : Math.sin(((progress - 0.6) / 0.4) * Math.PI) * 0.55;
  return { x, y: Math.max(0, y) };
}
